import { Fragment, useEffect, useMemo, useState } from "react";
import { LeadAPI } from "api/lead/service";
import {
  ChevronRight,
  Lock,
  Send,
  CheckCircle2,
  Clock,
  ChevronDown,
  MessageSquare,
  FileText,
  Trash2,
  Plus,
  Briefcase,
  MoreVertical,
  Pencil,
  ChevronUp,
  Download,
} from "lucide-react";
import { QuotationPDFPreviewModal } from "./QuotationPDFPreviewModal";

function toNumber(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatDateOnly(iso) {
  if (!iso) return "";
  return String(iso).split("T")[0];
}

function calcSubtotalFromRawQuotation(raw) {
  if (!raw) return 0;
  const items = Array.isArray(raw.items) ? raw.items : [];
  return items.reduce((total, it) => {
    const sub = Array.isArray(it.subitems) ? it.subitems : [];
    const subSum = sub.reduce((s, x) => s + toNumber(x.price), 0);
    return total + toNumber(it.price) + subSum;
  }, 0);
}

function calcTaxFromRawQuotation(raw) {
  if (!raw) return 0;
  const items = Array.isArray(raw.items) ? raw.items : [];
  return items.reduce((total, it) => {
    const sub = Array.isArray(it.subitems) ? it.subitems : [];
    const subSum = sub.reduce((s, x) => s + toNumber(x.tax), 0);
    return total + toNumber(it.tax) + subSum;
  }, 0);
}

export function QuotationView({ client, initialScopeId, canApprove = false }) {
  const [quotation, setQuotation] = useState(null);
  const [isLoadingQuotation, setIsLoadingQuotation] = useState(false);
  const [quotationError, setQuotationError] = useState("");
  const [quotationActionError, setQuotationActionError] = useState("");
  const [isWorkingOnQuotationAction, setIsWorkingOnQuotationAction] = useState(false);
  const [isIssueFlow, setIsIssueFlow] = useState(false);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [projectForm, setProjectForm] = useState({ site_name: "", esrimated_cost: "" });
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [customCharges, setCustomCharges] = useState([]);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [chargesError, setChargesError] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", price: "", tax: "" });
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [itemError, setItemError] = useState("");

  const [isAddSubitemOpen, setIsAddSubitemOpen] = useState(false);
  const [subitemParentItemId, setSubitemParentItemId] = useState(null);
  const [newSubitem, setNewSubitem] = useState({ name: "", price: "", tax: "" });
  const [isCreatingSubitem, setIsCreatingSubitem] = useState(false);
  const [subitemError, setSubitemError] = useState("");

  const [isAddChargeOpen, setIsAddChargeOpen] = useState(false);
  const [newCharge, setNewCharge] = useState({ label: "", amount: "" });
  const [isCreatingCharge, setIsCreatingCharge] = useState(false);

  const [isAddFeedbackOpen, setIsAddFeedbackOpen] = useState(false);
  const [newFeedback, setNewFeedback] = useState("");
  const [isCreatingFeedback, setIsCreatingFeedback] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // { type: "item"|"subitem"|"feedback", id: string }
  const [editModal, setEditModal] = useState(null); // { type, id, itemId? }
  const [editItem, setEditItem] = useState({ name: "", price: "", tax: "" });
  const [editSubitem, setEditSubitem] = useState({ name: "", price: "", tax: "" });
  const [editFeedback, setEditFeedback] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [quotationVersions, setQuotationVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState("");
  const [showNegotiationNote, setShowNegotiationNote] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState("");
  const [isUpdatingField, setIsUpdatingField] = useState(false);
  const [fieldError, setFieldError] = useState("");

  const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState(null);

  // Scope Selection State
  const [scopeDetails, setScopeDetails] = useState(null);
  const [isScopeExpanded, setIsScopeExpanded] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());
  const [isAddingScopeItems, setIsAddingScopeItems] = useState(false);

  const refreshQuotation = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    setQuotationError("");
    setIsLoadingQuotation(true);
    try {
      const res = await LeadAPI.getQuotationFull(orgId, leadId);
      const data = res?.data && typeof res?.success !== "undefined" ? res.data : res;
      setQuotation(data);
    } catch (e) {
      if (e?.status === 404) setQuotation(null);
      else setQuotationError(e?.message || "Failed to load quotation");
    } finally {
      setIsLoadingQuotation(false);
    }
  };

  const refreshCharges = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setChargesError("");
    setIsLoadingCharges(true);
    try {
      const res = await LeadAPI.listQuotationCustomCharges(orgId, leadId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setCustomCharges(list || []);
    } catch (e) {
      setChargesError(e?.message || "Failed to load custom charges");
    } finally {
      setIsLoadingCharges(false);
    }
  };

  const refreshFeedback = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setFeedbackError("");
    setIsLoadingFeedback(true);
    try {
      const res = await LeadAPI.listQuotationFeedback(orgId, leadId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setFeedback(list || []);
    } catch (e) {
      setFeedbackError(e?.message || "Failed to load negotiation feedback");
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const loadVersions = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setVersionsError("");
    setIsLoadingVersions(true);
    try {
      const res = await LeadAPI.listQuotationVersions(orgId, leadId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setQuotationVersions(Array.isArray(list) ? list : []);
    } catch (e) {
      setVersionsError(e?.message || "Failed to load version history");
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const startEditing = (field) => {
    if (isLocked) return;
    setFieldError("");
    setEditingField(field);
    if (field === "payment") {
      setFieldValue(uiQuotation?.paymentTerms || "");
    } else {
      setFieldValue(uiQuotation?.termsConditions || "");
    }
  };

  const saveField = async () => {
    if (!editingField) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const payload =
      editingField === "payment"
        ? { payment_terms: fieldValue }
        : { terms_conditions: fieldValue };
    setIsUpdatingField(true);
    setFieldError("");
    try {
      await LeadAPI.updateQuotation(orgId, leadId, payload);
      await refreshQuotation();
      setEditingField(null);
    } catch (e) {
      setFieldError(e?.message || "Failed to update");
    } finally {
      setIsUpdatingField(false);
    }
  };

  useEffect(() => {
    refreshQuotation();
    refreshCharges();
    refreshFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    const fetchScope = async () => {
      const orgId = LeadAPI.orgId();
      const leadId = client?.id;
      if (!orgId || !leadId) return;
      try {
        const res = await LeadAPI.getScopeFull(orgId, leadId);
        const data = res?.data && typeof res?.success !== "undefined" ? res.data : res;
        setScopeDetails(data);
      } catch (e) {
        console.error("Failed to load scope details", e);
      }
    };
    fetchScope();
  }, [client?.id]);

  const uiQuotation = useMemo(() => {
    if (!quotation) return null;
    const scopeInfo = quotation.scope_info || quotation.scopeInfo || {};
    const items = Array.isArray(quotation.items) ? quotation.items : [];
    const statusRaw = String(quotation.status || "draft").trim().toLowerCase();
    return {
      id: quotation.id,
      name: quotation.name,
      status: String(quotation.status || "draft").trim(),
      statusRaw,
      scopeId: quotation.scope,
      scopeName: scopeInfo.name || String(quotation.scope || ""),
      paymentTerms: quotation.payment_terms,
      termsConditions: quotation.terms_conditions,
      currentVersion: quotation.current_version,
      subtotal: quotation.subtotal,
      totalTax: quotation.total_tax,
      grandTotal: quotation.grand_total,
      items: items.map((it) => {
        const sub = Array.isArray(it.subitems) ? it.subitems : [];
        const subItems = sub.map((s) => ({
          id: s.id,
          name: s.name,
          price: toNumber(s.price),
          tax: toNumber(s.tax),
        }));

        return {
          id: it.id,
          name: it.name,
          price: toNumber(it.price),
          tax: toNumber(it.tax),
          subItems,
        };
      }),
      versions: quotation.versions || [],
    };
  }, [quotation]);

  const getStatusBadge = (status) => {
    const styles = {
      Draft: "bg-gray-200 text-gray-700",
      Open: "bg-gray-200 text-gray-700",
      "Sent for Approval": "bg-blue-100 text-blue-700",
      "Approver Approved": "bg-orange-100 text-orange-700",
      "Client Approved": "bg-green-100 text-green-700",
      Locked: "bg-sm-primary/10 text-sm-primary",
    };
    const normalized = String(status || "").trim();
    const label = normalized || "Draft";
    return (
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[label] || styles.Draft}`}
      >
        {label}
      </span>
    );
  };

  const calculateCustomCharges = () =>
    (Array.isArray(customCharges) ? customCharges : []).reduce((total, c) => total + toNumber(c.amount), 0);

  const calculateTotal = () =>
    calcSubtotalFromRawQuotation(quotation) + calcTaxFromRawQuotation(quotation) + calculateCustomCharges();

  const statusRaw = String(uiQuotation?.statusRaw || "").toLowerCase();
  const isLocked = statusRaw === "locked";
  const isApprovedAndLocked = isLocked; // conversion allowed only when locked in this workflow
  const addLockedStatuses = ["sent", "approver_approved", "client_approved", "locked"];
  const canAddItems = !addLockedStatuses.includes(statusRaw);
  const isLeadCompleted = String(client?.status || "").toLowerCase() === "completed";

  const headerStatusLabel = () => {
    if (canApprove) {
      if (statusRaw === "draft") return "Open";
      if (statusRaw === "sent") return "Sent for Approval";
      if (statusRaw === "approver_approved") return "Approver Approved";
      if (statusRaw === "client_approved") return "Client Approved";
      if (statusRaw === "locked") return "Locked";
      return uiQuotation?.status || "Open";
    }
    // non-approver
    if (statusRaw === "draft") return "Draft";
    if (statusRaw === "sent") return "Sent for Approval";
    if (statusRaw === "approver_approved") return "Approver Approved";
    if (statusRaw === "client_approved") return "Client Approved";
    if (statusRaw === "locked") return "Locked";
    return uiQuotation?.status || "Draft";
  };

  const runQuotationAction = async (fn, errorMsg) => {
    setQuotationActionError("");
    setIsWorkingOnQuotationAction(true);
    try {
      await fn();
      await refreshQuotation();
    } catch (e) {
      setQuotationActionError(e?.message || errorMsg);
    } finally {
      setIsWorkingOnQuotationAction(false);
    }
  };

  const sendQuotationForApproval = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(
      () => LeadAPI.sendQuotationForApproval(orgId, leadId),
      "Failed to send quotation for approval"
    );
  };

  const selfApproveAndSendToClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(
      () => LeadAPI.updateQuotation(orgId, leadId, { status: "approver_approved" }),
      "Failed to self approve and send to client"
    );
  };

  const approveQuotationAsApprover = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(
      () => LeadAPI.approveQuotationAsApprover(orgId, leadId),
      "Failed to approve quotation"
    );
  };

  const rejectQuotationAsApprover = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(
      () => LeadAPI.rejectQuotationAsApprover(orgId, leadId),
      "Failed to reject quotation"
    );
  };

  const approveQuotationAsClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(
      () => LeadAPI.approveQuotationAsClient(orgId, leadId),
      "Failed to mark client approved"
    );
  };

  const lockQuotation = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    await runQuotationAction(() => LeadAPI.lockQuotation(orgId, leadId), "Failed to lock quotation");
  };

  const openIssueFeedbackModal = () => {
    setFeedbackError("");
    setNewFeedback("");
    setIsIssueFlow(true);
    setIsAddFeedbackOpen(true);
  };

  const openCreateProjectModal = () => {
    const cost = toNumber(calculateTotal());
    setProjectForm({
      site_name: `Project - ${client?.name || ""}`.trim(),
      esrimated_cost: String(cost || ""),
    });
    setQuotationActionError("");
    setIsCreateProjectOpen(true);
  };

  const submitCreateProject = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const site_name = String(projectForm.site_name || "").trim();
    const esrimated_cost = toNumber(projectForm.esrimated_cost);
    if (!site_name) return;
    setQuotationActionError("");
    setIsCreatingProject(true);
    try {
      await LeadAPI.convertLeadToProject(orgId, leadId, { site_name, esrimated_cost, organization: orgId });
      setIsCreateProjectOpen(false);
    } catch (e) {
      setQuotationActionError(e?.message || "Failed to convert to project");
    } finally {
      setIsCreatingProject(false);
    }
  };

  // If no quotation exists yet, show empty state
  if (!uiQuotation) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          No Quotation Created Yet
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Create a quotation after the scope of work is approved.
        </p>
        {quotationError ? (
          <p className="text-xs text-red-600 mb-2">{quotationError}</p>
        ) : null}
        {isLoadingQuotation ? (
          <p className="text-xs text-gray-400 mb-2">Loading quotation...</p>
        ) : null}
        <p className="text-xs text-gray-400">
          Use the "Proceed to Quotation" button from the Scope of Work tab.
        </p>
      </div>
    );
  }

  const handleCreateItem = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization")).id;
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const name = newItem.name.trim();
    const price = toNumber(newItem.price);
    const tax = toNumber(newItem.tax);
    if (!name) return;
    setIsCreatingItem(true);
    setItemError("");
    try {
      await LeadAPI.createQuotationItem(orgId, leadId, {
        quotation: Number(uiQuotation.id),
        name,
        price,
        tax,
      });
      setIsAddItemOpen(false);
      setNewItem({ name: "", price: "", tax: "" });
      await refreshQuotation();
    } catch (e) {
      setItemError(e?.message || "Failed to add item");
    } finally {
      setIsCreatingItem(false);
    }
  };

  const handleCreateSubitem = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const itemId = subitemParentItemId;
    const name = newSubitem.name.trim();
    const price = toNumber(newSubitem.price);
    const tax = toNumber(newSubitem.tax);
    if (!itemId || !name) return;

    setIsCreatingSubitem(true);
    setSubitemError("");
    try {
      await LeadAPI.createQuotationSubitem(orgId, leadId, itemId, {
        item: Number(itemId),
        name,
        price,
        tax,
      });
      setIsAddSubitemOpen(false);
      setSubitemParentItemId(null);
      setNewSubitem({ name: "", price: "", tax: "" });
      await refreshQuotation();
    } catch (e) {
      setSubitemError(e?.message || "Failed to add subitem");
    } finally {
      setIsCreatingSubitem(false);
    }
  };

  const handleCreateCharge = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const label = newCharge.label.trim();
    const amount = toNumber(newCharge.amount);
    if (!label) return;
    setIsCreatingCharge(true);
    setChargesError("");
    try {
      await LeadAPI.createQuotationCustomCharge(orgId, leadId, { label, amount });
      setIsAddChargeOpen(false);
      setNewCharge({ label: "", amount: "" });
      await refreshCharges();
      await refreshQuotation();
    } catch (e) {
      setChargesError(e?.message || "Failed to add charge");
    } finally {
      setIsCreatingCharge(false);
    }
  };

  const handleCreateFeedback = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const message = newFeedback.trim();
    if (!message) return;
    setIsCreatingFeedback(true);
    setFeedbackError("");
    try {
      await LeadAPI.createQuotationFeedback(orgId, leadId, { message });
      setIsAddFeedbackOpen(false);
      setNewFeedback("");
      if (isIssueFlow) {
        await LeadAPI.reopenQuotation(orgId, leadId);
        await refreshQuotation();
        setIsIssueFlow(false);
      }
      await refreshFeedback();
    } catch (e) {
      setFeedbackError(e?.message || "Failed to save feedback");
    } finally {
      setIsCreatingFeedback(false);
    }
  };

  const openEdit = (type, payload) => {
    setEditError("");
    if (type === "item") {
      setEditModal({ type: "item", id: String(payload.id) });
      setEditItem({
        name: payload.name || "",
        price: String(payload.price ?? ""),
        tax: String(payload.tax ?? ""),
      });
    } else if (type === "subitem") {
      setEditModal({ type: "subitem", id: String(payload.id), itemId: payload.itemId });
      setEditSubitem({
        name: payload.name || "",
        price: String(payload.price ?? ""),
        tax: String(payload.tax ?? ""),
      });
    } else if (type === "feedback") {
      setEditModal({ type: "feedback", id: String(payload.id) });
      setEditFeedback(payload.message || "");
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    setIsSavingEdit(true);
    setEditError("");
    try {
      if (editModal.type === "item") {
        const payload = {
          name: String(editItem.name || "").trim(),
          price: toNumber(editItem.price),
          tax: toNumber(editItem.tax),
        };
        if (!payload.name) return;
        setQuotation((prev) => {
          if (!prev) return prev;
          const data = prev?.data && typeof prev?.success !== "undefined" ? prev.data : prev;
          if (!Array.isArray(data.items)) return prev;
          const next = {
            ...data,
            items: data.items.map((it) => (String(it.id) === String(editModal.id) ? { ...it, ...payload } : it)),
          };
          return prev?.data && typeof prev?.success !== "undefined" ? { ...prev, data: next } : next;
        });
        await LeadAPI.updateQuotationItem(orgId, leadId, editModal.id, payload);
        await refreshQuotation();
      } else if (editModal.type === "subitem") {
        const payload = {
          name: String(editSubitem.name || "").trim(),
          price: toNumber(editSubitem.price),
          tax: toNumber(editSubitem.tax),
        };
        if (!payload.name) return;
        setQuotation((prev) => {
          if (!prev) return prev;
          const data = prev?.data && typeof prev?.success !== "undefined" ? prev.data : prev;
          if (!Array.isArray(data.items)) return prev;
          const next = {
            ...data,
            items: data.items.map((it) => ({
              ...it,
              subitems: Array.isArray(it.subitems)
                ? it.subitems.map((s) => (String(s.id) === String(editModal.id) ? { ...s, ...payload } : s))
                : it.subitems,
            })),
          };
          return prev?.data && typeof prev?.success !== "undefined" ? { ...prev, data: next } : next;
        });
        await LeadAPI.updateQuotationSubitem(orgId, leadId, editModal.id, payload);
        await refreshQuotation();
      } else if (editModal.type === "feedback") {
        const message = String(editFeedback || "").trim();
        if (!message) return;
        setFeedback((prev) =>
          (Array.isArray(prev) ? prev : []).map((f) => (String(f.id) === String(editModal.id) ? { ...f, message } : f))
        );
        await LeadAPI.updateQuotationFeedback(orgId, leadId, editModal.id, { message });
        await refreshFeedback();
      }
      setEditModal(null);
    } catch (e) {
      setEditError(e?.message || "Failed to update");
      // revert via refresh
      await refreshQuotation().catch(() => { });
      await refreshFeedback().catch(() => { });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleCategory = (catId, reqIds = []) => {
    const newCats = new Set(selectedCategories);
    const newReqs = new Set(selectedRequirements);

    if (newCats.has(catId)) {
      newCats.delete(catId);
      // Deselect all children
      reqIds.forEach((id) => newReqs.delete(id));
    } else {
      newCats.add(catId);
      // Select all children
      reqIds.forEach((id) => newReqs.add(id));
    }
    setSelectedCategories(newCats);
    setSelectedRequirements(newReqs);
  };

  const handleToggleRequirement = (catId, reqId) => {
    const newReqs = new Set(selectedRequirements);
    const newCats = new Set(selectedCategories);

    if (newReqs.has(reqId)) {
      newReqs.delete(reqId);
    } else {
      newReqs.add(reqId);
      newCats.add(catId); // Auto-select parent
    }
    setSelectedRequirements(newReqs);
    setSelectedCategories(newCats);
  };

  const addSelectedScopeItems = async () => {
    if (selectedCategories.size === 0) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    setIsAddingScopeItems(true);
    try {
      const categories = scopeDetails?.categories || [];
      for (const cat of categories) {
        if (selectedCategories.has(String(cat.id))) {
          // Find or Create Item
          let targetItemId = null;
          // uiQuotation.items has local generated IDs or string IDs.
          // We match by name for now, as that's the semantic link.
          const existingItem = uiQuotation.items.find((i) => i.name === cat.name);

          if (existingItem) {
            targetItemId = existingItem.id;
          } else {
            // Create Item
            const res = await LeadAPI.createQuotationItem(orgId, leadId, {
              quotation: Number(uiQuotation.id),
              name: cat.name,
              price: 0,
              tax: 0,
            });
            const created = res?.data && typeof res?.success !== "undefined" ? res.data : res;
            // If we just created it, we might need to refresh or trust the ID.
            // But if we continue the loop, we might re-create if we don't update local reference?
            // No, existingItem check is against `uiQuotation` which is stale during this loop.
            // However, duplication by name is acceptable or we should avoid it.
            // If we really want to avoid duplicates *during* this batch, we should track created items.
            // But for simplicity, we assume user adds different categories or we just rely on API.
            // The API likely returns the ID.
            targetItemId = created.id;
          }

          // Process Requirements
          const reqs = cat.requirements || [];
          for (const req of reqs) {
            if (selectedRequirements.has(String(req.id))) {
              // Add Subitem
              const subName = `${req.field || ""} ${req.value || ""}`.trim() || "Requirement";

              // Optional: Check if subitem with this name exists in targetItemId?
              // Since we don't have the fresh item details if we just created it, difficult.
              // We'll just add it.
              await LeadAPI.createQuotationSubitem(orgId, leadId, targetItemId, {
                item: Number(targetItemId),
                name: subName,
                price: 0,
                tax: 0
              });
            }
          }
        }
      }

      await refreshQuotation();
      // Reset Selection
      setSelectedCategories(new Set());
      setSelectedRequirements(new Set());
      setIsScopeExpanded(false);
    } catch (e) {
      console.error(e);
      setQuotationActionError(e?.message || "Failed to add scope items");
    } finally {
      setIsAddingScopeItems(false);
    }
  };

  const handleDeleteEntity = async (type, id) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    try {
      if (type === "item") {
        setQuotation((prev) => {
          if (!prev) return prev;
          const data = prev?.data && typeof prev?.success !== "undefined" ? prev.data : prev;
          if (!Array.isArray(data.items)) return prev;
          const next = { ...data, items: data.items.filter((it) => String(it.id) !== String(id)) };
          return prev?.data && typeof prev?.success !== "undefined" ? { ...prev, data: next } : next;
        });
        await LeadAPI.deleteQuotationItem(orgId, leadId, id);
        await refreshQuotation();
      } else if (type === "subitem") {
        setQuotation((prev) => {
          if (!prev) return prev;
          const data = prev?.data && typeof prev?.success !== "undefined" ? prev.data : prev;
          if (!Array.isArray(data.items)) return prev;
          const next = {
            ...data,
            items: data.items.map((it) => ({
              ...it,
              subitems: Array.isArray(it.subitems) ? it.subitems.filter((s) => String(s.id) !== String(id)) : it.subitems,
            })),
          };
          return prev?.data && typeof prev?.success !== "undefined" ? { ...prev, data: next } : next;
        });
        await LeadAPI.deleteQuotationSubitem(orgId, leadId, id);
        await refreshQuotation();
      } else if (type === "feedback") {
        setFeedback((prev) => (Array.isArray(prev) ? prev : []).filter((f) => String(f.id) !== String(id)));
        await LeadAPI.deleteQuotationFeedback(orgId, leadId, id);
        await refreshFeedback();
      }
    } catch (_) {
      await refreshQuotation().catch(() => { });
      await refreshFeedback().catch(() => { });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
            <span>{client.name}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-900 font-medium">Quotation</span>
          </div>
          <h1 className="text-lg font-bold text-gray-900">
            {uiQuotation.name}
          </h1>
        </div>
        <div className="flex gap-1.5">
          <button
            className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => {
              try {
                const orgStr = localStorage.getItem("organization");
                if (orgStr) {
                  setOrganizationDetails(JSON.parse(orgStr));
                }
              } catch (e) {
                console.error("Failed to parse organization", e);
              }
              setIsPDFPreviewOpen(true);
            }}
          >
            <Download className="w-3 h-3" />
            Download PDF
          </button>
          {/* Non-approver */}
          {(!canApprove && (statusRaw === "draft" || statusRaw === "rejected")) ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnQuotationAction}
              onClick={sendQuotationForApproval}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnQuotationAction ? "Sending..." : "Send for Approval"}
            </button>
          ) : null}
          {(canApprove && (statusRaw === "draft" || statusRaw === "rejected")) ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnQuotationAction}
              onClick={selfApproveAndSendToClient}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnQuotationAction ? "Sending..." : "Approve & Send to Client"}
            </button>
          ) : null}
          {/* Approver */}
          {canApprove && statusRaw === "sent" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnQuotationAction}
                onClick={approveQuotationAsApprover}
              >
                <CheckCircle2 className="w-3 h-3" />
                {isWorkingOnQuotationAction ? "Working..." : "Approve Quotation"}
              </button>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-red-300 text-red-700 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-red-50"
                  }`}
                disabled={isWorkingOnQuotationAction}
                onClick={rejectQuotationAsApprover}
              >
                Reject Quotation
              </button>
            </>
          ) : null}

          {canApprove && statusRaw === "approver_approved" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnQuotationAction}
                onClick={approveQuotationAsClient}
              >
                <CheckCircle2 className="w-3 h-3" />
                Client Approved
              </button>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnQuotationAction}
                onClick={openIssueFeedbackModal}
              >
                <MessageSquare className="w-3 h-3" />
                Feedback Issue
              </button>
            </>
          ) : null}

          {canApprove && statusRaw === "client_approved" ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnQuotationAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnQuotationAction}
              onClick={lockQuotation}
            >
              <Lock className="w-3 h-3" />
              Lock
            </button>
          ) : null}

          <button
            className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium text-white rounded-md transition-colors ${isApprovedAndLocked
              ? (isLeadCompleted ? "bg-gray-300 cursor-not-allowed" : "bg-sm-primary hover:bg-sm-hover")
              : "bg-gray-300 cursor-not-allowed"
              }`}
            disabled={!isApprovedAndLocked || !canApprove || isLeadCompleted}
            onClick={() => {
              if (!canApprove) return;
              if (!isApprovedAndLocked) return;
              if (isLeadCompleted) return;
              openCreateProjectModal();
            }}
          >
            <Briefcase className="w-3 h-3" />
            Create Project
          </button>
        </div>
      </div>

      {/* Full Width Quotation Details */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white rounded-md border border-gray-200 p-4">
          {quotationActionError ? (
            <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
              {quotationActionError}
            </div>
          ) : null}
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {getStatusBadge(headerStatusLabel())}
              <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-gray-200 rounded-full text-gray-600">
                <FileText className="w-3 h-3" />
                {uiQuotation.scopeName}
              </span>
            </div>
            {canApprove && statusRaw !== "locked" ? (
              <p className="text-[10px] text-sm-accent bg-sm-accent/10 px-2 py-0.5 rounded">
                Complete approvals and lock to create project
              </p>
            ) : null}
          </div>

          {/* Negotiation Note */}
          {showNegotiationNote && (
            <div className="mb-3 p-2.5 bg-blue-50 border border-blue-100 rounded flex items-start gap-2">
              <MessageSquare className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600">
                  Client can send remarks for negotiation after receiving this
                  quotation.
                </p>
              </div>
              <button
                onClick={() => setShowNegotiationNote(false)}
                className="h-6 px-2 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Scope of Work Import Section */}
          {canAddItems && scopeDetails && (
            <div className="mb-4 bg-white border border-gray-200 rounded-md overflow-hidden">
              <button
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                onClick={() => setIsScopeExpanded(!isScopeExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">
                    Import from Scope of Work
                  </span>
                </div>
                {isScopeExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>

              {isScopeExpanded && (
                <div className="p-3 border-t border-gray-200">
                  <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
                    {(scopeDetails.categories || []).length === 0 && (
                      <p className="text-xs text-gray-400 italic">No categories found in Scope of Work.</p>
                    )}
                    {(scopeDetails.categories || []).map((cat) => (
                      <div key={cat.id} className="space-y-1">
                        <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            checked={selectedCategories.has(String(cat.id))}
                            onChange={() =>
                              handleToggleCategory(
                                String(cat.id),
                                (cat.requirements || []).map((r) => String(r.id))
                              )
                            }
                            className="rounded border-gray-300 text-sm-primary focus:ring-sm-primary"
                          />
                          <span className="text-xs font-medium text-gray-900">
                            {cat.name}
                          </span>
                        </div>
                        <div className="pl-6 space-y-1">
                          {(cat.requirements || []).map((req) => (
                            <div key={req.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedRequirements.has(String(req.id))}
                                onChange={() =>
                                  handleToggleRequirement(String(cat.id), String(req.id))
                                }
                                className="rounded border-gray-300 text-sm-primary focus:ring-sm-primary"
                              />
                              <span className="text-xs text-gray-600">
                                {req.field}: {req.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-2 border-t border-gray-200">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded transition-colors ${isAddingScopeItems || selectedCategories.size === 0
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                        }`}
                      onClick={addSelectedScopeItems}
                      disabled={isAddingScopeItems || selectedCategories.size === 0}
                    >
                      {isAddingScopeItems ? "Adding..." : "Add Selected to Quotation"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Items Table */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Items</h3>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${canAddItems ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                  }`}
                onClick={() => {
                  if (!canAddItems) return;
                  setItemError("");
                  setNewItem({ name: "", price: "", tax: "" });
                  setIsAddItemOpen(true);
                }}
                disabled={!canAddItems}
              >
                <Plus className="w-3 h-3" />
                Add Item
              </button>
            </div>

            <div className="border border-gray-200 rounded overflow-visible">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                      Items
                    </th>
                    <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                      SubItems
                    </th>
                    <th className="text-right text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                      Price
                    </th>
                    <th className="text-right text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                      Tax
                    </th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {uiQuotation.items.map((item) => {
                    const itemPrice = Number(item.price || 0);
                    const itemTax = Number(item.tax || 0);
                    // const hasItemPrice = itemPrice > 0;
                    const subItems = item.subItems || [];
                    const rowsToRender = [];
                    // if (hasItemPrice) {
                    rowsToRender.push({ type: 'base', data: item });
                    // }
                    subItems.forEach(s => rowsToRender.push({ type: 'sub', data: s }));

                    const totalRows = rowsToRender.length;

                    // Totals
                    const subItemsTotal = subItems.reduce((acc, curr) => acc + Number(curr.price || 0), 0);
                    const subItemsTax = subItems.reduce((acc, curr) => acc + Number(curr.tax || 0), 0);
                    const sectionTotalPrice = itemPrice + subItemsTotal;
                    const sectionTotalTax = itemTax + subItemsTax;

                    // If no rows (no price, no subitems), skip rendering (or handle empty state if needed)
                    // if (totalRows === 0) return null;

                    return (
                      <Fragment key={item.id}>
                        {rowsToRender.map((row, index) => (
                          <tr key={row.type === 'base' ? `base-${item.id}` : row.data.id} className="border-t border-gray-200">
                            {index === 0 && (
                              <td
                                className="px-2.5 py-1.5 text-xs font-medium align-top"
                                rowSpan={totalRows}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="flex items-center gap-2">
                                    {item.name}
                                    <div className="relative">

                                      <button
                                        className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                        onClick={() =>
                                          setOpenMenu((prev) =>
                                            prev?.type === "item" && prev?.id === String(item.id)
                                              ? null
                                              : { type: "item", id: String(item.id) }
                                          )
                                        }
                                      >
                                        <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                                      </button>
                                      {openMenu?.type === "item" && openMenu?.id === String(item.id) ? (
                                        <div className="absolute left-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-20">
                                          <button
                                            className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                            onClick={() => {
                                              setOpenMenu(null);
                                              // derive real item (from server) if available
                                              const raw = (quotation?.data && typeof quotation?.success !== "undefined"
                                                ? quotation.data?.items
                                                : quotation?.items) || [];
                                              const serverItem = Array.isArray(raw)
                                                ? raw.find((x) => String(x.id) === String(item.id))
                                                : null;
                                              openEdit("item", {
                                                id: item.id,
                                                name: item.name,
                                                price: serverItem?.price ?? "",
                                                tax: serverItem?.tax ?? "",
                                              });
                                            }}
                                          >
                                            <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                            Edit
                                          </button>
                                          <button
                                            className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                            onClick={() => {
                                              setOpenMenu(null);
                                              handleDeleteEntity("item", item.id);
                                            }}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete
                                          </button>
                                        </div>
                                      ) : null}
                                    </div>
                                  </span>
                                  <button
                                    className={`h-6 px-2 text-[10px] border border-gray-300 rounded-md transition-colors ${canAddItems ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                                      }`}
                                    onClick={() => {
                                      if (!canAddItems) return;
                                      setSubitemError("");
                                      setSubitemParentItemId(item.id);
                                      setNewSubitem({ name: "", price: "", tax: "" });
                                      setIsAddSubitemOpen(true);
                                    }}
                                    disabled={!canAddItems}
                                  >
                                    + Subitem
                                  </button>
                                </div>
                              </td>
                            )}

                            {/* Render Cells */}
                            {row.type === 'base' ? (
                              <>
                                <td className="px-2.5 py-1.5 text-xs text-gray-500 italic">
                                  Base Cost
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-right">
                                  ₹{itemPrice.toLocaleString()}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-right text-gray-500">
                                  ₹{itemTax.toLocaleString()}
                                </td>
                                <td className="px-1"></td>
                              </>
                            ) : (
                              <>
                                <td className="px-2.5 py-1.5 text-xs text-gray-500">
                                  {row.data.name}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-right">
                                  ₹{Number(row.data.price).toLocaleString()}
                                </td>
                                <td className="px-2.5 py-1.5 text-xs text-right text-gray-500">
                                  ₹{Number(row.data.tax).toLocaleString()}
                                </td>
                                <td className="px-1">
                                  {String(row.data.id).includes("-self") ? null : (
                                    <div className="relative">
                                      <button
                                        className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                        onClick={() =>
                                          setOpenMenu((prev) =>
                                            prev?.type === "subitem" && prev?.id === String(row.data.id)
                                              ? null
                                              : { type: "subitem", id: String(row.data.id) }
                                          )
                                        }
                                      >
                                        <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                                      </button>
                                      {openMenu?.type === "subitem" && openMenu?.id === String(row.data.id) ? (
                                        <div className="absolute right-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-20">
                                          <button
                                            className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                                            onClick={() => {
                                              setOpenMenu(null);
                                              openEdit("subitem", {
                                                id: row.data.id,
                                                itemId: item.id,
                                                name: row.data.name,
                                                price: row.data.price,
                                                tax: row.data.tax,
                                              });
                                            }}
                                          >
                                            <Pencil className="w-3.5 h-3.5 text-gray-500" />
                                            Edit
                                          </button>
                                          <button
                                            className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                            onClick={() => {
                                              setOpenMenu(null);
                                              handleDeleteEntity("subitem", row.data.id);
                                            }}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete
                                          </button>
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}

                        {/* Section Total Row */}
                        <tr className="bg-gray-50/80 border-t border-gray-200">
                          <td colSpan={2} className="px-2.5 py-1.5 text-[10px] font-bold text-gray-600 text-right uppercase tracking-wider">
                            Total
                          </td>
                          <td className="px-2.5 py-1.5 text-xs font-semibold text-right text-gray-900 border-l border-gray-100">
                            ₹{sectionTotalPrice.toLocaleString()}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs font-medium text-right text-gray-500 border-l border-gray-100">
                            ₹{sectionTotalTax.toLocaleString()}
                          </td>
                          <td></td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {itemError ? (
              <div className="mt-2 text-xs text-red-600">{itemError}</div>
            ) : null}
          </div>

          {/* Custom Charges */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Custom Charges
              </h3>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                onClick={() => {
                  if (isLocked) return;
                  setChargesError("");
                  setNewCharge({ label: "", amount: "" });
                  setIsAddChargeOpen(true);
                }}
                disabled={isLocked}
              >
                <Plus className="w-3 h-3" />
                Add Charge
              </button>
            </div>
            <div className="space-y-1">
              {isLoadingCharges ? (
                <div className="text-xs text-gray-500">Loading charges...</div>
              ) : null}
              {(Array.isArray(customCharges) ? customCharges : []).map((charge, index) => (
                <div
                  key={charge.id || index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs"
                >
                  <span>{charge.label}</span>
                  <span className="font-medium">₹{toNumber(charge.amount).toLocaleString()}</span>
                </div>
              ))}
            </div>
            {chargesError ? (
              <div className="mt-2 text-xs text-red-600">{chargesError}</div>
            ) : null}
          </div>

          {/* Totals */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span>
                  ₹{calcSubtotalFromRawQuotation(quotation).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tax</span>
                <span>
                  ₹{calcTaxFromRawQuotation(quotation).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Charges</span>
                <span>
                  ₹{calculateCustomCharges().toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200">
                <span>Total</span>
                <span className="text-sm-primary">
                  ₹{calculateTotal().toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Negotiation / Client Feedback */}
          <div className="mb-4 p-2.5 bg-sm-accent/10 border border-sm-accent/30 rounded">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-sm-accent" />
              <h4 className="text-xs font-medium text-sm-accent">
                Negotiation Feedback
              </h4>
              <button
                className="ml-auto h-6 px-2 flex items-center gap-1 text-[10px] font-medium border border-sm-accent/30 rounded hover:bg-sm-accent/10 transition-colors"
                onClick={() => {
                  setFeedbackError("");
                  setNewFeedback("");
                  setIsAddFeedbackOpen(true);
                }}
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
            {feedbackError ? (
              <div className="text-[10px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5 mb-2">
                {feedbackError}
              </div>
            ) : null}
            {isLoadingFeedback ? (
              <div className="text-xs text-gray-500">Loading feedback...</div>
            ) : null}
            {!isLoadingFeedback && (Array.isArray(feedback) ? feedback : []).length === 0 ? (
              <div className="text-xs text-gray-600">No feedback yet.</div>
            ) : null}
            {(Array.isArray(feedback) ? feedback : []).map((fb) => (
              <div key={fb.id || fb.message} className="text-xs flex items-start justify-between gap-2">
                <div>
                  <p className="text-gray-900">{fb.message}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {formatDateOnly(fb.created_on || fb.created_at || fb.date)}
                    {fb.created_by ? ` • ${fb.created_by}` : ""}
                  </p>
                </div>
                <div className="relative">
                  <button
                    className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-sm-accent/10 transition-colors"
                    onClick={() =>
                      setOpenMenu((prev) =>
                        prev?.type === "feedback" && prev?.id === String(fb.id)
                          ? null
                          : { type: "feedback", id: String(fb.id) }
                      )
                    }
                  >
                    <MoreVertical className="w-3.5 h-3.5 text-sm-accent" />
                  </button>
                  {openMenu?.type === "feedback" && openMenu?.id === String(fb.id) ? (
                    <div className="absolute right-0 bottom-full mb-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-20">
                      <button
                        className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => {
                          setOpenMenu(null);
                          openEdit("feedback", fb);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5 text-gray-500" />
                        Edit
                      </button>
                      <button
                        className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                        onClick={() => {
                          setOpenMenu(null);
                          handleDeleteEntity("feedback", fb.id);
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {/* Edit Modal (Item/Subitem/Feedback) */}
          {editModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    Edit {editModal.type === "item" ? "Item" : editModal.type === "subitem" ? "Subitem" : "Feedback"}
                  </h3>
                  <button
                    onClick={() => setEditModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingEdit}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {editError ? (
                    <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                      {editError}
                    </div>
                  ) : null}

                  {(editModal.type === "item" || editModal.type === "subitem") && (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Name *</label>
                        <input
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                          value={editModal.type === "item" ? editItem.name : editSubitem.name}
                          onChange={(e) =>
                            editModal.type === "item"
                              ? setEditItem({ ...editItem, name: e.target.value })
                              : setEditSubitem({ ...editSubitem, name: e.target.value })
                          }
                          disabled={isSavingEdit}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-700">Price</label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                            value={editModal.type === "item" ? editItem.price : editSubitem.price}
                            onChange={(e) =>
                              editModal.type === "item"
                                ? setEditItem({ ...editItem, price: e.target.value })
                                : setEditSubitem({ ...editSubitem, price: e.target.value })
                            }
                            disabled={isSavingEdit}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-700">Tax</label>
                          <input
                            type="number"
                            step="0.01"
                            className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                            value={editModal.type === "item" ? editItem.tax : editSubitem.tax}
                            onChange={(e) =>
                              editModal.type === "item"
                                ? setEditItem({ ...editItem, tax: e.target.value })
                                : setEditSubitem({ ...editSubitem, tax: e.target.value })
                            }
                            disabled={isSavingEdit}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {editModal.type === "feedback" && (
                    <textarea
                      className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                      value={editFeedback}
                      onChange={(e) => setEditFeedback(e.target.value)}
                      disabled={isSavingEdit}
                    />
                  )}
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditModal(null)}
                    disabled={isSavingEdit}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingEdit ? "bg-gray-300 cursor-not-allowed" : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={handleSaveEdit}
                    disabled={isSavingEdit}
                  >
                    {isSavingEdit ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Terms */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <h4 className="text-[10px] font-medium text-gray-900 uppercase tracking-wide mb-1">
                Payment Terms
              </h4>
              {editingField === "payment" ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full min-h-[60px] p-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    disabled={isUpdatingField}
                  />
                  {fieldError ? (
                    <p className="text-[10px] text-red-600">{fieldError}</p>
                  ) : null}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="px-2 py-1 text-[10px] font-medium rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                      onClick={() => setEditingField(null)}
                      disabled={isUpdatingField}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-2 py-1 text-[10px] font-medium rounded bg-sm-primary text-white hover:bg-sm-hover transition-colors"
                      onClick={saveField}
                      disabled={isUpdatingField}
                    >
                      {isUpdatingField ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 p-2 bg-gray-50 rounded flex items-center justify-between gap-2">
                  <span>{uiQuotation.paymentTerms || "Not specified"}</span>
                  {!isLocked && (
                    <button
                      className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                      onClick={() => startEditing("payment")}
                    >
                      <Pencil className="w-3 h-3 text-gray-500" />
                    </button>
                  )}
                </p>
              )}
            </div>
            <div>
              <h4 className="text-[10px] font-medium text-gray-900 uppercase tracking-wide mb-1">
                Terms & Conditions
              </h4>
              {editingField === "terms" ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full min-h-[60px] p-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    disabled={isUpdatingField}
                  />
                  {fieldError ? (
                    <p className="text-[10px] text-red-600">{fieldError}</p>
                  ) : null}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="px-2 py-1 text-[10px] font-medium rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                      onClick={() => setEditingField(null)}
                      disabled={isUpdatingField}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-2 py-1 text-[10px] font-medium rounded bg-sm-primary text-white hover:bg-sm-hover transition-colors"
                      onClick={saveField}
                      disabled={isUpdatingField}
                    >
                      {isUpdatingField ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 p-2 bg-gray-50 rounded flex items-center justify-between gap-2">
                  <span>{uiQuotation.termsConditions || "Not specified"}</span>
                  {!isLocked && (
                    <button
                      className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                      onClick={() => startEditing("terms")}
                    >
                      <Pencil className="w-3 h-3 text-gray-500" />
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Version History */}
          <div className="mt-4">
            <button
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
              onClick={async () => {
                const next = !showVersionHistory;
                setShowVersionHistory(next);
                if (next) await loadVersions();
              }}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Version History</span>
              {showVersionHistory ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
            {showVersionHistory && (
              <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                {versionsError ? (
                  <div className="text-xs text-red-600">{versionsError}</div>
                ) : null}
                {isLoadingVersions ? (
                  <div className="text-xs text-gray-500">Loading versions...</div>
                ) : null}
                {!isLoadingVersions && (Array.isArray(quotationVersions) ? quotationVersions : []).length === 0 ? (
                  <div className="text-xs text-gray-500">No versions yet.</div>
                ) : null}
                {(Array.isArray(quotationVersions) ? quotationVersions : []).map((v, idx) => (
                  <div key={v.id || v.version_number || idx} className="relative">
                    <div className="absolute -left-[1.125rem] w-2 h-2 rounded-full bg-sm-primary border-2 border-white" />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-xs">
                          v{v.version_number ?? v.number ?? v.version ?? idx + 1}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {formatDateOnly(v.created_at || v.created_on || v.createdAt || v.date || "")}
                          {v.created_by ? ` • ${v.created_by}` : ""}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {v.changes_description || v.changes || v.message || v.notes || ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      {isAddItemOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Add Item</h3>
              <button
                onClick={() => setIsAddItemOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                disabled={isCreatingItem}
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Name *</label>
                <input
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  disabled={isCreatingItem}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                    disabled={isCreatingItem}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Tax</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newItem.tax}
                    onChange={(e) => setNewItem({ ...newItem, tax: e.target.value })}
                    disabled={isCreatingItem}
                  />
                </div>
              </div>
              {itemError ? <div className="text-xs text-red-600">{itemError}</div> : null}
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setIsAddItemOpen(false)}
                disabled={isCreatingItem}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingItem || !newItem.name.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                onClick={handleCreateItem}
                disabled={isCreatingItem || !newItem.name.trim()}
              >
                {isCreatingItem ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subitem Modal */}
      {isAddSubitemOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Add Subitem</h3>
              <button
                onClick={() => setIsAddSubitemOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                disabled={isCreatingSubitem}
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Name *</label>
                <input
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={newSubitem.name}
                  onChange={(e) => setNewSubitem({ ...newSubitem, name: e.target.value })}
                  disabled={isCreatingSubitem}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newSubitem.price}
                    onChange={(e) => setNewSubitem({ ...newSubitem, price: e.target.value })}
                    disabled={isCreatingSubitem}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-gray-700">Tax</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                    value={newSubitem.tax}
                    onChange={(e) => setNewSubitem({ ...newSubitem, tax: e.target.value })}
                    disabled={isCreatingSubitem}
                  />
                </div>
              </div>
              {subitemError ? <div className="text-xs text-red-600">{subitemError}</div> : null}
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setIsAddSubitemOpen(false)}
                disabled={isCreatingSubitem}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingSubitem || !newSubitem.name.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                onClick={handleCreateSubitem}
                disabled={isCreatingSubitem || !newSubitem.name.trim()}
              >
                {isCreatingSubitem ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Charge Modal */}
      {isAddChargeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Add Charge</h3>
              <button
                onClick={() => setIsAddChargeOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                disabled={isCreatingCharge}
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Label *</label>
                <input
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={newCharge.label}
                  onChange={(e) => setNewCharge({ ...newCharge, label: e.target.value })}
                  disabled={isCreatingCharge}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={newCharge.amount}
                  onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                  disabled={isCreatingCharge}
                />
              </div>
              {chargesError ? <div className="text-xs text-red-600">{chargesError}</div> : null}
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setIsAddChargeOpen(false)}
                disabled={isCreatingCharge}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingCharge || !newCharge.label.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                onClick={handleCreateCharge}
                disabled={isCreatingCharge || !newCharge.label.trim()}
              >
                {isCreatingCharge ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Feedback Modal */}
      {isAddFeedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                {isIssueFlow ? "Feedback Issue" : "Add Feedback"}
              </h3>
              <button
                onClick={() => {
                  setIsAddFeedbackOpen(false);
                  setIsIssueFlow(false);
                }}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                disabled={isCreatingFeedback}
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Message *</label>
                <textarea
                  className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                  value={newFeedback}
                  onChange={(e) => setNewFeedback(e.target.value)}
                  disabled={isCreatingFeedback}
                />
              </div>
              {feedbackError ? <div className="text-xs text-red-600">{feedbackError}</div> : null}
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsAddFeedbackOpen(false);
                  setIsIssueFlow(false);
                }}
                disabled={isCreatingFeedback}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingFeedback || !newFeedback.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                onClick={handleCreateFeedback}
                disabled={isCreatingFeedback || !newFeedback.trim()}
              >
                {isCreatingFeedback ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {isCreateProjectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Create Project</h3>
              <button
                onClick={() => setIsCreateProjectOpen(false)}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                disabled={isCreatingProject}
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Site Name *</label>
                <input
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={projectForm.site_name}
                  onChange={(e) => setProjectForm((p) => ({ ...p, site_name: e.target.value }))}
                  disabled={isCreatingProject}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700">Estimated Cost</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={projectForm.esrimated_cost}
                  onChange={(e) => setProjectForm((p) => ({ ...p, esrimated_cost: e.target.value }))}
                  disabled={isCreatingProject}
                />
              </div>
            </div>
            <div className="p-4 bg-gray-50 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setIsCreateProjectOpen(false)}
                disabled={isCreatingProject}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingProject || !String(projectForm.site_name || "").trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                onClick={submitCreateProject}
                disabled={isCreatingProject || !String(projectForm.site_name || "").trim()}
              >
                {isCreatingProject ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
      <QuotationPDFPreviewModal
        open={isPDFPreviewOpen}
        onClose={() => setIsPDFPreviewOpen(false)}
        quotation={uiQuotation}
        client={client}
        customCharges={customCharges}
        organization={organizationDetails}
      />
    </div>
  );
}
